/**
 * MeetingDetailReadOnlyModal — M-Rhythm-2 / M-Rhythm-3
 *
 * Read-only view of a single meeting: title, time, accountable,
 * participants (resolved), agenda items, status badge.
 *
 * M-Rhythm-3: CB-003 — raw UUIDs replaced with "(unknown)" / "(deleted actor)"
 * when actor resolution fails.
 * F-MUT2 AC-MUT2-7: Moderator row removed (system Meeting Moderator always assigned server-side).
 * F-MUT2 AC-MUT2-6: Origin-aware completion navigation via location.state.returnTo.
 * F-MUT2 AC-MUT2-1: Participant role labels removed from overview list.
 *
 * Uses boilerplate Modal primitive.
 * Compliance: M-Rhythm-2 plan §5 task 8 / M-Rhythm-3 CB-003.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import { formatDateTimeStr } from '@/lib/localeFormatters';
import { apiFetch } from '@/lib/api';
import type {
  CalendarMeetingDTO,
  MeetingStatus,
  RorosMeetingParticipantDTO,
  RorosMeetingAgendaItemDTO,
  RorosMeetingDTO,
} from '@/types/roros/meetings';
import { isVirtualMeeting } from '@/types/roros/meetings';
import { statusVariant, statusLabel } from './meeting-status-utils';
import { useRorosActor, useRorosActors } from '@/hooks/roros/actors';
import { useRorosMeeting } from '@/hooks/roros/meetings';
import type { RorosActorDTO } from '@/types/roros/actors';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuthStore } from '@/stores/authStore';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function meetingTypeLabel(type: string): string {
  const map: Record<string, string> = {
    daily:     t('Daily Check-in'),
    weekly:    t('Weekly'),
    monthly:   t('Monthly'),
    quarterly: t('Quarterly'),
    trimester: t('Trimester'),
    annual:    t('Annual'),
    ad_hoc:    t('Ad hoc'),
  };
  return map[type] ?? type;
}

/**
 * CB-003: Resolve a display name for an actor or domain reference.
 * When the actor/domain name fails to resolve, show "(unknown)" instead
 * of the raw UUID.
 */
function resolveDisplayName(
  displayName: string | null | undefined,
  actorId: string | null | undefined,
  domainId: string | null | undefined,
): string {
  if (displayName && displayName.trim()) return displayName;
  // No resolved name — don't show the UUID, show a placeholder
  if (actorId || domainId) return t('(deleted actor)');
  return t('(unknown)');
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface MeetingDetailReadOnlyModalProps {
  meeting: CalendarMeetingDTO;
  isOpen: boolean;
  onClose: () => void;
  /** Optional callback to open the edit modal (replaces disabled "Edit" button when provided). */
  onEdit?: () => void;
}

const ACTOR_DIRECTORY_PARAMS = { pageSize: '100', sortBy: 'name', sortOrder: 'asc' } as const;

export function MeetingDetailReadOnlyModal({
  meeting,
  isOpen,
  onClose,
  onEdit,
}: MeetingDetailReadOnlyModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isStartingFacilitation, setIsStartingFacilitation] = useState(false);
  const isVirtual = isVirtualMeeting(meeting);

  // AC-4A-1: Fetch full meeting details (incl. agendaItems) when a real meeting ID is
  // available. The calendar list view omits agenda items for performance; the detail
  // modal must load them separately.
  const fullMeetingQuery = useRorosMeeting(meeting.id ?? '');
  const fullMeeting = fullMeetingQuery.data as RorosMeetingDTO | undefined;

  const participants = (meeting.participants ?? []) as RorosMeetingParticipantDTO[];
  // AC-MUT1-6: Use agenda items from the full meeting fetch (GET /api/v1/meetings/:id)
  // which always includes agendaItems. Fall back to the calendar DTO value (may be [])
  // while the fetch is loading or when no real ID is present (virtual meetings).
  // Prefer fullMeeting data to ensure the overview and edit dialog are always in sync.
  const agendaItems = (
    (fullMeeting?.agendaItems ?? meeting.agendaItems) ?? []
  ) as RorosMeetingAgendaItemDTO[];

  // F-MUT1 FW2: name resolution beyond the first 20 actors — without pageSize the
  // overview showed "(deleted actor)" for any actor not on page 1 (F-MUT1-P9).
  const actorsQuery = useRorosActors({ pageSize: '100', sortBy: 'name', sortOrder: 'asc' });
  const actors = (actorsQuery.data ?? []) as RorosActorDTO[];
  const accountableActorQuery = useRorosActor(meeting.accountableActorId ?? '');
  const actorNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const a of actors) {
      map.set(a.id, a.name);
    }
    return map;
  }, [actors]);

  // FW2-6: Permission and accountable-actor checks
  const { hasPermission } = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id ?? null;
  const currentUserActorQuery = useRorosActors(
    currentUser?.email
      ? { ...ACTOR_DIRECTORY_PARAMS, q: currentUser.email }
      : { ...ACTOR_DIRECTORY_PARAMS },
  );
  const currentUserActors = (currentUserActorQuery.data ?? []) as RorosActorDTO[];
  const canManageMeetings = hasPermission('roros.meetings.manage');

  // FW2-6: Check if current user is the meeting's accountable actor.
  const currentUserActorId = React.useMemo(() => {
    if (!currentUserId) return null;
    return (
      actors.find((a) => a.userId === currentUserId)?.id
      ?? currentUserActors.find((a) => a.userId === currentUserId)?.id
      ?? null
    );
  }, [actors, currentUserActors, currentUserId]);
  const isAccountableForMeeting = !!meeting.accountableActorId && currentUserActorId === meeting.accountableActorId;

  // Format scheduled_at for display
  const scheduledDate = meeting.scheduledAt
    ? formatDateTimeStr(new Date(meeting.scheduledAt))
    : '—';

  // AC-MUT1-5 + AC-MUT1-6: Duration display.
  // 1. Prefer the full meeting fetch (fullMeeting) over the calendar DTO (meeting)
  //    so that the overview and edit dialog always show the same persisted value.
  // 2. Fall back to the agenda-item sum when no explicit duration is stored.
  //    The edit dialog computes this live; mirroring the same logic here keeps
  //    both views consistent.
  // 3. Only show "—" when neither an explicit duration nor any agenda-item
  //    durations exist.
  const durationDisplay = React.useMemo(() => {
    const explicitMinutes = fullMeeting?.durationMinutes ?? meeting.durationMinutes;
    if (explicitMinutes) return `${explicitMinutes} ${t('min')}`;
    const agendaSum = agendaItems.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0);
    if (agendaSum > 0) return `${agendaSum} ${t('min')}`;
    return '—';
  }, [fullMeeting?.durationMinutes, meeting.durationMinutes, agendaItems]);

  // FW2-6: Run Meeting visible only for scheduled, non-virtual meetings where
  // caller has ROROS_MEETINGS_MANAGE permission OR is the meeting's accountable actor.
  const canRunMeeting = meeting.status === 'scheduled'
    && !isVirtual
    && !!meeting.id
    && (canManageMeetings || isAccountableForMeeting);

  async function handleRunMeeting() {
    if (!meeting.id) return;
    setIsStartingFacilitation(true);
    try {
      const res = await apiFetch(`/api/v1/meetings/${meeting.id}/start-facilitation`, {
        method: 'POST',
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({})) as { error?: { message?: string } };
        throw new Error(errJson.error?.message ?? t('Failed to start facilitation'));
      }
      const json = await res.json() as { data: RorosMeetingDTO };
      // AC-MUT2-6: pass current page as returnTo so the wizard finish link comes back here
      navigate(`/roros/meetings/${json.data.id}/run`, {
        state: { returnTo: location.pathname + location.search },
      });
    } catch (err) {
      console.error('[MeetingDetailReadOnlyModal] start-facilitation error', err);
    } finally {
      setIsStartingFacilitation(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {meeting.title}
          {meeting.isRecurringException && (
            <span className="rounded bg-warning-100 text-warning-700 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide">
              {t('Edited')}
            </span>
          )}
          {/* Wave 4B: Customized badge — shown when user has manually overridden any template field */}
          {((meeting as RorosMeetingDTO).manualFieldOverrides ?? []).length > 0 && (
            <span
              className="rounded bg-blue-100 text-blue-700 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
              data-testid="meeting-detail-customized-badge"
            >
              {t('Customized')}
            </span>
          )}
        </span>
      }
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            {onEdit ? (
              <Button
                variant="outline"
                onClick={onEdit}
                aria-label={t('Edit meeting')}
              >
                {t('Edit meeting')}
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled
                title={t('You do not have permission to edit meetings')}
                aria-label={t('Edit meeting')}
              >
                {t('Edit meeting')}
              </Button>
            )}
            {/* AC-4.4: Run Meeting button — visible only for scheduled non-virtual meetings */}
            {canRunMeeting && (
              <Button
                variant="primary"
                onClick={handleRunMeeting}
                isLoading={isStartingFacilitation}
                leftIcon={<Play className="h-4 w-4" />}
                aria-label={t('Run Meeting')}
              >
                {t('Run Meeting')}
              </Button>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>
            {t('Close')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        {/* Status + type row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={statusVariant(meeting.status as MeetingStatus)}>
            {statusLabel(meeting.status as MeetingStatus)}
          </Badge>
          <span className="text-muted-foreground text-xs">
            {meetingTypeLabel(meeting.meetingType)}
          </span>
          {isVirtual && (
            <span className="text-xs text-muted-foreground italic">
              ({t('Virtual (not yet materialized)')})
            </span>
          )}
        </div>

        {/* Scheduled at + duration */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('Scheduled')}</p>
            <p className="font-medium">{scheduledDate}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('Duration')}</p>
            <p className="font-medium">{durationDisplay}</p>
          </div>
        </div>

        {/* Accountable — CB-003: show (deleted actor) instead of raw UUID */}
        {(meeting.accountableActorId || meeting.accountableDomainId) && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('Accountable')}</p>
            <p>
              {meeting.accountableDomainId
                ? t('(as Domain Accountable)')
                : resolveDisplayName(
                    accountableActorQuery.data?.name
                      ?? (meeting.accountableActorId ? actorNameMap.get(meeting.accountableActorId) ?? null : null),
                    meeting.accountableActorId,
                    null,
                  )}
              {meeting.accountableDomainId && (
                <span className="text-muted-foreground text-xs ml-1">({t('as Domain Accountable')})</span>
              )}
            </p>
          </div>
        )}

        {/* AC-MUT2-7: Moderator row removed — system Meeting Moderator always assigned server-side */}

        {/* Participants */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t('Participants')}</p>
          {participants.length === 0 ? (
            <p className="text-muted-foreground text-xs">{t('No participants')}</p>
          ) : (
            <ul className="space-y-1">
              {participants.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  {/* CB-003: use resolveDisplayName instead of raw ID */}
                  <span>
                    {resolveDisplayName(p.displayName, p.actorId, p.domainId)}
                  </span>
                  {p.participantKind === 'domain_accountable' && (
                    <span className="text-xs text-muted-foreground">({t('as Domain Accountable')})</span>
                  )}
                  {/* AC-MUT2-1: role label removed — attendee/observer concept removed from UI */}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Agenda — AC-4A-1: Loaded from full meeting fetch; shows items + per-item durations + total */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{t('Agenda')}</p>
          {fullMeetingQuery.isLoading && !isVirtual && meeting.id ? (
            <p className="text-muted-foreground text-xs">{t('Loading agenda...')}</p>
          ) : agendaItems.length === 0 ? (
            <p className="text-muted-foreground text-xs">{t('No agenda items')}</p>
          ) : (
            <>
              <ol className="space-y-1">
                {agendaItems.map((item, idx) => (
                  <li key={item.id} className="flex items-start gap-2">
                    <span className="text-muted-foreground text-xs mt-0.5">{idx + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <span>{item.title}</span>
                      {item.durationMinutes != null ? (
                        <span className="text-muted-foreground text-xs ml-2">{item.durationMinutes} {t('min')}</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
              {/* Total duration row — only shown when at least one item has a duration */}
              {agendaItems.some((item) => item.durationMinutes != null) && (
                <p className="text-xs text-muted-foreground mt-1 font-medium">
                  {t('meeting::Total')}: {agendaItems.reduce((sum, item) => sum + (item.durationMinutes ?? 0), 0)} {t('min')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Skipped reason */}
        {meeting.status === 'skipped' && meeting.skippedReason && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t('Skip reason')}</p>
            <p className="text-sm italic text-muted-foreground">{meeting.skippedReason}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
